import React, { useState } from "react";
import { useDispatch } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useCompleteNewPasswordMutation } from "../../app/store/api/authApi";
import { setCredentials } from "../../app/store/authSlice";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { decodeIdToken } from "../utils/decodeIdToken";
import { useNavigate } from "react-router-dom";
import { getRouteForUser } from "../utils/roleRoutes";

const PASSWORD_RULES = {
  minLength: 8,
  hasUpper: /[A-Z]/,
  hasLower: /[a-z]/,
  hasNumber: /[0-9]/,
  hasSpecial: /[^A-Za-z0-9]/,
};

function validatePassword(password) {
  if (password.length < PASSWORD_RULES.minLength) {
    return `Password must be at least ${PASSWORD_RULES.minLength} characters`;
  }
  if (!PASSWORD_RULES.hasUpper.test(password)) {
    return "Password must include at least one uppercase letter";
  }
  if (!PASSWORD_RULES.hasLower.test(password)) {
    return "Password must include at least one lowercase letter";
  }
  if (!PASSWORD_RULES.hasNumber.test(password)) {
    return "Password must include at least one number";
  }
  if (!PASSWORD_RULES.hasSpecial.test(password)) {
    return "Password must include at least one special character";
  }
  return null;
}

export default function NewPasswordDialog({ open, username, session, onClose, onSuccess }) {

  const {t, i18n } = useTranslation();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");

  const dispatch = useDispatch();
  const [completeNewPassword, { isLoading }] = useCompleteNewPasswordMutation();
  const navigate = useNavigate()

  const handleClose = () => {
    if (isLoading) return; // prevent closing mid-request
    setNewPassword("");
    setConfirmPassword("");
    setError("");
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const validationError = validatePassword(newPassword);
    if (validationError) {
      setError(validationError);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    toast.promise(
        async () => {
            const result = await completeNewPassword({
                username,
                newPassword,
                session,
            }).unwrap();

            const decodedUser = decodeIdToken(result.idToken);
            dispatch(
              setCredentials({
                accessToken: result.accessToken,
                idToken: result.idToken,
              })
            );
      
            setNewPassword("");
            setConfirmPassword("");
            onSuccess?.();
            navigate(getRouteForUser(decodedUser), { replace: true });
        },
        {
            loading: t("setNewPassword.loading"),
            success: t("setNewPassword.success"),
            error: (err) => {
                setError(err?.data?.message[i18n.language] || t("setNewPassword.error"));
                return err?.data?.message[i18n.language] || t("setNewPassword.error");
            }
        }
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      component="form"
      onSubmit={handleSubmit}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        {t("setNewPassword.title")}
        <IconButton onClick={handleClose} disabled={isLoading} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("setNewPassword.subTitle")}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          label={t("setNewPassword.newPassword")}
          type={showPassword ? "text" : "password"}
          fullWidth
          margin="normal"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          autoFocus
          disabled={isLoading}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword((v) => !v)} edge="end" size="small">
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label={t("setNewPassword.confirmNewPassword")}
          type={showConfirm ? "text" : "password"}
          fullWidth
          margin="normal"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          disabled={isLoading}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowConfirm((v) => !v)} edge="end" size="small">
                  {showConfirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
          {t("setNewPassword.validationText")}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={isLoading}>
          {t("common.cancel")}
        </Button>
        <Button type="submit" variant="contained" disabled={isLoading}>
          {isLoading ? <CircularProgress size={24} color="inherit" /> : t("setNewPassword.submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}